import { useState } from "react";
import { svgToPngBlob } from "../download";

type Props = {
  svg: string;
  filename: string;
  /** Gọi sau khi `navigator.share()` thành công thật (không gọi khi người dùng tự huỷ). */
  onShared?: () => void;
};

/**
 * Chia sẻ + Sao chép liên kết.
 *
 * LƯU Ý TRUNG THỰC: sản phẩm CHƯA có trang chia sẻ công khai riêng cho từng thiết kế
 * (`/s/{shareId}` — docs/72-growth-viral.md §3) — đã xác nhận `GET /api/v1/shares` trả 404 và
 * không có bảng/route `shares` nào ở apps/api hiện tại. Vì vậy:
 * - "Chia sẻ" dùng `navigator.share({ files })` để chia sẻ THẲNG ẢNH đã tạo (không có link OG
 *   đẹp kèm theo, vì chưa có trang nào để trỏ tới).
 * - "Sao chép liên kết" chỉ sao chép được liên kết TRANG CHỦ chung của QR Studio (không phải
 *   liên kết riêng cho thiết kế này) — nói rõ điều này trong copy để không đánh lừa người dùng.
 */
export default function ShareActions({ svg, filename, onShared }: Props) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const canShareFiles = typeof navigator !== "undefined" && typeof navigator.share === "function";

  function handleShare() {
    setBusy(true);
    setMsg(null);
    svgToPngBlob(svg)
      .then(async (blob) => {
        const file = new File([blob], filename, { type: "image/png" });
        if (typeof navigator.canShare === "function" && !navigator.canShare({ files: [file] })) {
          throw new Error("cannot_share_files");
        }
        await navigator.share({ files: [file], title: "QR Studio" });
        onShared?.();
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === "AbortError") return; // người dùng tự huỷ
        setMsg("Trình duyệt này chưa hỗ trợ chia sẻ ảnh trực tiếp. Bạn tải ảnh về rồi chia sẻ thủ công nhé.");
      })
      .finally(() => setBusy(false));
  }

  function handleCopyLink() {
    const url = typeof window !== "undefined" ? window.location.origin : "https://qrstudio.cuong.io.vn";
    navigator.clipboard
      ?.writeText(url)
      .then(() => setMsg("Đã sao chép liên kết QR Studio (trang riêng cho mẫu này chưa có)."))
      .catch(() => setMsg("Chưa sao chép được, bạn tự copy link trên thanh địa chỉ nhé."));
  }

  return (
    <div className="share-actions">
      {canShareFiles ? (
        <button type="button" className="btn btn-secondary btn-block" onClick={handleShare} disabled={busy}>
          {busy ? "Đang chuẩn bị…" : "Chia sẻ"}
        </button>
      ) : null}
      <button type="button" className="btn btn-secondary btn-block" onClick={handleCopyLink}>
        Sao chép liên kết
      </button>
      {msg ? <p className="hint-text small">{msg}</p> : null}
    </div>
  );
}
